import { useCallback, useState } from 'react';
import { ITooltipProps } from './tooltip/useSingleChartDataTooltip';

export const CHART_OPACITY_ACTIVE = 1;
export const CHART_OPACITY_INACTIVE = 0.3;

/**
 * this hook is used to handle the opacity of the bars
 * in BarChart, FunnelChart, StackedBarChart.
 *
 * The goal is when we display any tooltips, we only make the impacted bars visible
 * as opacity CHART_OPACITY_ACTIVE.
 * for the rest of bars, use CHART_OPACITY_INACTIVE opacity.
 */
export const useBarChartOpacity = () => {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  const getBarOpacity = useCallback(
    ({
      tooltipProps,
      xAxisValue,
      lineName,
      isTooltipLocked,
      disableRechartTooltip,
    }: {
      tooltipProps: ITooltipProps | null;
      xAxisValue: string;
      lineName: string;
      isTooltipLocked: boolean;
      disableRechartTooltip?: boolean;
    }) => {
      // if tooltip is on hovered bar,
      const isLineHovered = tooltipProps?.line?.name === lineName;
      // if tooltip is on hovered date,
      const isDateHovered = tooltipProps?.date === xAxisValue;
      // if Single Tooltip is OFF, and hover on entire xAxis date
      // display all bars belong to this date with ACTIVE opacity
      const activeBar =
        !isTooltipLocked &&
        (!activeLabel || (activeLabel === xAxisValue && !tooltipProps));

      // If Single Tooltip is ON, show ACTIVE opacity for hovered bar
      const isBarHovered = isLineHovered && isDateHovered;

      // special case: for simple(no breakdown and (query or funnel freq or ttc)) chart,
      // we need to disable rechart tooltip, therefore need to set the opacity to active when hover on the date
      if (disableRechartTooltip && !isLineHovered) {
        return CHART_OPACITY_ACTIVE;
      }

      return activeBar || isBarHovered
        ? CHART_OPACITY_ACTIVE
        : CHART_OPACITY_INACTIVE;
    },
    [activeLabel],
  );
  return { setActiveLabel, getBarOpacity };
};
